// frontend/src/components/SizeCalculatorSpot.tsx
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Stack,
  Text,
  useDisclosure,
  HStack,
  IconButton,
  ButtonGroup,
  Wrap,
  WrapItem,
  useBreakpointValue,
  Tag,
  Skeleton
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { CopyIcon, RepeatIcon } from '@chakra-ui/icons'
import { useToast } from '@chakra-ui/react'
import { getCoinKrwPrice } from '../services/apiService'
import { useTranslation } from 'react-i18next'

const SIZE_KRW = 5000 as const

const AVAILABLE_SYMBOLS = ['ETH', 'DOGE', 'SOL', 'BTC', 'XRP'] as const
type SpotSymbol = (typeof AVAILABLE_SYMBOLS)[number]

const KOR_COIN_LABEL: Record<SpotSymbol, string> = {
  ETH: '이더리움',
  DOGE: '도지',
  SOL: '솔라나',
  BTC: '비트코인',
  XRP: '리플'
}

type SizeCalculatorSpotProps = {
  /** 부모에서 현재 선택 심볼을 넘기면 내부 심볼 선택 UI는 그대로 동작하지만 초기값/동기화에 사용됨 */
  symbol?: SpotSymbol
  /** 부모에서 현재가(KRW)를 넘기면 내부에서 그대로 사용 (없으면 자체 조회) */
  priceKrwFromParent?: number | null
  /** 부모에서 새로고침 로직을 갖고 있다면 넘겨주면 됨(선택) */
  onRefreshPrice?: (symbol: SpotSymbol) => void | Promise<void>
}

const SizeCalculatorSpot = ({
  symbol: symbolProp,
  priceKrwFromParent,
  onRefreshPrice
}: SizeCalculatorSpotProps) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, md: false })

  // i18n 코인 라벨 헬퍼 (en에서는 Ethereum 등으로 표시)
  const coinLabel = (sym: SpotSymbol) =>
    t(`coin.${sym}`, { defaultValue: KOR_COIN_LABEL[sym] })

  // ✅ 심볼 상태 (부모 값 우선, 없으면 ETH)
  const [symbol, setSymbol] = useState<SpotSymbol>(symbolProp || 'ETH')

  // ✅ 사이즈(개수)만 입력
  const [sizeCount, setSizeCount] = useState('1')

  // ✅ 가격(KRW)
  const [priceKrw, setPriceKrw] = useState<number | null>(
    priceKrwFromParent ?? null
  )
  const [loadingPrice, setLoadingPrice] = useState(false)

  // 부모 symbol 변경 시 내부 동기화
  useEffect(() => {
    if (symbolProp && AVAILABLE_SYMBOLS.includes(symbolProp)) {
      setSymbol(symbolProp)
    }
  }, [symbolProp])

  // 부모 가격 전달 시 반영
  useEffect(() => {
    if (typeof priceKrwFromParent === 'number' || priceKrwFromParent === null) {
      setPriceKrw(priceKrwFromParent)
    }
  }, [priceKrwFromParent])

  // 내부 조회 함수
  const fetchKrwPrice = async (sym: SpotSymbol) => {
    try {
      setLoadingPrice(true)
      const { data: res } = await getCoinKrwPrice(sym)
      if (res?.statusCode === 200) {
        setPriceKrw(res.data ?? null)
      } else {
        setPriceKrw(null)
        toast({
          title: t('가격 조회 실패'),
          description: t('{{name}} 가격을 불러오지 못했습니다.', {
            name: coinLabel(sym)
          }),
          status: 'error',
          duration: 2500,
          isClosable: true
        })
      }
    } catch {
      setPriceKrw(null)
      toast({
        title: t('오류'),
        description: t('{{name}} 가격 조회 중 오류가 발생했습니다.', {
          name: coinLabel(sym)
        }),
        status: 'error',
        duration: 2500,
        isClosable: true
      })
    } finally {
      setLoadingPrice(false)
    }
  }

  // 공통 새로고침 핸들러: 부모가 제공하면 부모 호출, 아니면 내부 fetch
  const refreshPrice = async (sym: SpotSymbol) => {
    if (onRefreshPrice) {
      await onRefreshPrice(sym)
    } else {
      await fetchKrwPrice(sym)
    }
  }

  // 모달이 열릴 때 가격 없으면 조회
  useEffect(() => {
    if (isOpen && (priceKrw === null || priceKrw === undefined)) {
      refreshPrice(symbol)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // ✅ 심볼 바뀌면 항상 갱신 (부모 있으면 부모로, 없으면 내부로)
  useEffect(() => {
    if (!isOpen) return
    refreshPrice(symbol)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol])

  const parsedSize = Math.max(1, Number(sizeCount || '1'))
  const totalKrw = parsedSize * SIZE_KRW
  const estimatedAmount = useMemo(() => {
    if (!priceKrw || priceKrw <= 0) return 0
    return totalKrw / priceKrw
  }, [totalKrw, priceKrw])

  const handleCopySize = () => {
    navigator.clipboard.writeText(String(parsedSize))
    toast({
      title: t('사이즈 복사 완료'),
      description: t('"{{value}}" 이(가) 클립보드에 복사되었습니다.', {
        value: parsedSize
      }),
      status: 'success',
      duration: 2000,
      isClosable: true
    })
  }

  return (
    <>
      <Button variant="outline" colorScheme="purple" onClick={onOpen} w="100%">
        {t('수량계산기 (현물)')}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('수량계산기')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={5}>
              {/* 코인 선택 */}
              <Stack spacing={2}>
                <Text fontWeight="medium">{t('거래 코인')}</Text>
                {isMobile ? (
                  <Wrap spacing={2}>
                    {AVAILABLE_SYMBOLS.map((sym) => (
                      <WrapItem key={sym}>
                        <Button
                          size="sm"
                          variant={symbol === sym ? 'solid' : 'outline'}
                          colorScheme={symbol === sym ? 'purple' : 'gray'}
                          onClick={() => setSymbol(sym)}
                        >
                          {coinLabel(sym)} ({sym})
                        </Button>
                      </WrapItem>
                    ))}
                  </Wrap>
                ) : (
                  <ButtonGroup isAttached>
                    {AVAILABLE_SYMBOLS.map((sym) => (
                      <Button
                        key={sym}
                        size="sm"
                        variant={symbol === sym ? 'solid' : 'outline'}
                        colorScheme={symbol === sym ? 'purple' : 'gray'}
                        onClick={() => setSymbol(sym)}
                      >
                        {coinLabel(sym)} ({sym})
                      </Button>
                    ))}
                  </ButtonGroup>
                )}
              </Stack>

              {/* 현재가 (KRW) */}
              <Stack>
                <HStack justify="space-between" align="center">
                  <Text fontWeight="medium">
                    {t('현재 {{name}} 가격 (KRW)', { name: coinLabel(symbol) })}
                  </Text>
                  <IconButton
                    aria-label={t('가격 새로고침')}
                    icon={<RepeatIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => refreshPrice(symbol)}
                  />
                </HStack>
                <Skeleton isLoaded={!loadingPrice}>
                  <Input
                    type="text"
                    value={
                      priceKrw !== null && priceKrw !== undefined
                        ? priceKrw.toLocaleString()
                        : '-'
                    }
                    readOnly
                    bg="gray.100"
                  />
                </Skeleton>
                {priceKrw ? (
                  <Tag size="sm" colorScheme="purple" w="fit-content">
                    {priceKrw.toLocaleString()} KRW / {symbol}
                  </Tag>
                ) : null}
              </Stack>

              {/* 사이즈 개수 */}
              <Stack>
                <Text fontWeight="medium">{t('사이즈 개수')}</Text>
                <HStack flexWrap="wrap">
                  {[1, 2, 3, 5, 10, 20, 30, 50].map((val) => (
                    <Button
                      key={val}
                      size="xs"
                      variant={Number(sizeCount) === val ? 'solid' : 'outline'}
                      colorScheme={
                        Number(sizeCount) === val ? 'purple' : 'gray'
                      }
                      onClick={() => setSizeCount(String(val))}
                    >
                      {val}
                    </Button>
                  ))}
                </HStack>
                <Input
                  type="number"
                  min={1}
                  value={sizeCount}
                  onChange={(e) => setSizeCount(e.target.value)}
                  onBlur={() => {
                    if (!sizeCount || Number(sizeCount) < 1) setSizeCount('1')
                  }}
                />
                <Text fontSize="sm" color="gray.600">
                  {t('* 1 size 당 주문금액은 {{amount}} KRW 입니다.', {
                    amount: SIZE_KRW.toLocaleString()
                  })}
                </Text>
              </Stack>

              {/* 결과 */}
              <Stack
                mt={2}
                spacing={4}
                p={4}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                bg="gray.50"
              >
                <Text fontWeight="bold" fontSize="lg">
                  {t('계산 결과')}
                </Text>

                <Stack spacing={1}>
                  <HStack justify="space-between">
                    <Text color="gray.600">{t('📌 총 주문 금액')}</Text>
                    <Text fontWeight="semibold">
                      {totalKrw.toLocaleString()} KRW
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.600">{t('📌 예상 매수 수량')}</Text>
                    <Text fontWeight="semibold">
                      {estimatedAmount.toFixed(6)} {symbol}
                    </Text>
                  </HStack>

                  <HStack justify="space-between" align="center">
                    <Text color="gray.600">{t('📌 자동매매용 사이즈 값')}</Text>
                    <HStack>
                      <Text fontWeight="semibold" color="purple.600">
                        {parsedSize} size
                      </Text>
                      <IconButton
                        aria-label={t('사이즈 복사')}
                        size="xs"
                        icon={<CopyIcon />}
                        onClick={handleCopySize}
                        variant="ghost"
                        colorScheme="purple"
                      />
                    </HStack>
                  </HStack>
                </Stack>

                <Text fontSize="sm" color="gray.500">
                  {t(
                    '* 실제 체결 수량은 슬리피지/수수료에 따라 달라질 수 있습니다.'
                  )}
                </Text>
              </Stack>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>{t('닫기')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default SizeCalculatorSpot
